/** Client-side key normalization (mirrors server/productAiAdmin heuristics). */

export const HYPHEN_ALLOWLIST = new Set(['screen-print', 'hand-drawn', 'line-art']);
const MAX_TOKEN_LENGTH = 24;
const CLAUSE_IN_TOKEN = /\s+with\s+/i;

export function normalizeAttrKey(s) {
  return String(s || '').trim().toLowerCase();
}

export function simpleSingularToken(token) {
  const w = String(token || '').toLowerCase();
  if (w.length < 3) return w;
  if (w.endsWith('ies') && w.length > 4) return `${w.slice(0, -3)}y`;
  if (w.endsWith('ses') && w.length > 4) return w.slice(0, -2);
  if (w.endsWith('s') && !w.endsWith('ss')) return w.slice(0, -1);
  return w;
}

export function normalizePhraseKey(phrase) {
  return String(phrase || '')
    .trim()
    .toLowerCase()
    .split(/\s+/)
    .filter(Boolean)
    .map(simpleSingularToken)
    .join(' ');
}

export function valueKeyForField(field, displayValue) {
  return field === 'colors' ? normalizeAttrKey(displayValue) : normalizePhraseKey(displayValue);
}

function isValidToken(token) {
  const t = String(token || '').trim();
  if (!t || t.length < 2) return false;
  if (t.length > MAX_TOKEN_LENGTH) return false;
  if (/\s/.test(t)) return false;
  return true;
}

/** Split a tag string into atomic tokens (mirrors vision ingest tokenizeTagEntry). */
export function tokenizeTagEntry(raw) {
  const trimmed = String(raw || '').trim();
  if (!trimmed) return [];
  if (CLAUSE_IN_TOKEN.test(trimmed)) return [];

  const tokens = [];
  const parts = trimmed.split(/\s+/).filter(Boolean);
  for (const part of parts) {
    const lower = part.toLowerCase();
    if (HYPHEN_ALLOWLIST.has(lower)) {
      tokens.push(lower);
      continue;
    }
    const subparts = lower.split(/-+/).filter(Boolean);
    if (subparts.length <= 1) {
      tokens.push(lower);
      continue;
    }
    let i = 0;
    while (i < subparts.length) {
      let matched = false;
      for (const allowed of HYPHEN_ALLOWLIST) {
        const n = allowed.split('-').length;
        if (i + n > subparts.length) continue;
        const slice = subparts.slice(i, i + n).join('-');
        if (slice === allowed) {
          tokens.push(allowed);
          i += n;
          matched = true;
          break;
        }
      }
      if (!matched) {
        tokens.push(subparts[i]);
        i += 1;
      }
    }
  }
  return tokens.filter(isValidToken);
}

export function filterValuesByContains(values, query) {
  const q = String(query || '').trim().toLowerCase();
  if (!q) return values || [];
  return (values || []).filter((v) => String(v.value || '').toLowerCase().includes(q));
}

let searchGroupIdCounter = 0;

export function createSearchGroup(orTerms = ['']) {
  searchGroupIdCounter += 1;
  return { id: `sg-${searchGroupIdCounter}`, orTerms: [...orTerms] };
}

/** @returns {{ andGroups: { id: string, orTerms: string[] }[] }} */
export function createEmptySearchQuery() {
  return { andGroups: [createSearchGroup([''])] };
}

export function normalizeSearchTerm(term) {
  return String(term || '').trim().toLowerCase();
}

/** Tag matches if every AND group has at least one OR term found as substring in the tag. */
export function tagMatchesSearchQuery(tagValue, query) {
  const raw = String(tagValue || '').toLowerCase();
  if (!raw) return false;
  const groups = query?.andGroups || [];
  const activeGroups = groups
    .map((g) => (g.orTerms || []).map(normalizeSearchTerm).filter(Boolean))
    .filter((terms) => terms.length > 0);
  if (!activeGroups.length) return false;
  return activeGroups.every((terms) => terms.some((t) => raw.includes(t)));
}

export function hasActiveSearchQuery(query) {
  return (query?.andGroups || []).some((g) =>
    (g.orTerms || []).some((t) => normalizeSearchTerm(t))
  );
}

export function filterValuesBySearchQuery(values, query) {
  const list = values || [];
  if (!hasActiveSearchQuery(query)) return list;
  return list.filter((v) => tagMatchesSearchQuery(v.value, query));
}

/**
 * Parse expressions like `(florida OR state) AND outline` into AND/OR groups.
 * @param {string} text
 */
export function parseSearchQueryExpression(text) {
  const raw = String(text || '').trim();
  if (!raw) return createEmptySearchQuery();

  const andParts = raw.split(/\s+AND\s+/i).map((p) => p.trim()).filter(Boolean);
  const andGroups = andParts.map((part) => {
    let segment = part.trim();
    if (segment.startsWith('(') && segment.endsWith(')')) {
      segment = segment.slice(1, -1).trim();
    }
    const orTerms = segment
      .split(/\s+OR\s+/i)
      .map((t) => t.trim())
      .filter(Boolean);
    return createSearchGroup(orTerms.length ? orTerms : ['']);
  });
  return { andGroups: andGroups.length ? andGroups : [createSearchGroup([''])] };
}

/** Human-readable summary of a structured query. */
export function formatSearchQueryExpression(query) {
  const parts = (query?.andGroups || [])
    .map((g) => {
      const terms = (g.orTerms || []).map((t) => t.trim()).filter(Boolean);
      if (!terms.length) return '';
      if (terms.length === 1) return terms[0];
      return `(${terms.join(' OR ')})`;
    })
    .filter(Boolean);
  return parts.join(' AND ');
}

/** Build a query from a shared-word hint (single term). */
export function searchQueryFromTerm(term) {
  const t = String(term || '').trim();
  return { andGroups: [createSearchGroup(t ? [t] : [''])] };
}

/**
 * Words appearing in >= minVariations distinct tag values.
 * @param {Array<{ key: string, value: string, product_count?: number }>} values
 * @param {{ minVariations?: number }} [opts]
 */
export function buildSharedWordHints(values, opts = {}) {
  const minVariations = opts.minVariations ?? 3;
  const wordToTags = new Map();

  for (const v of values || []) {
    const tagKey = v.key || valueKeyForField('themes', v.value);
    const tagValue = String(v.value || '').trim();
    if (!tagValue) continue;

    const seenWords = new Set();
    for (const token of tokenizeTagEntry(tagValue)) {
      seenWords.add(simpleSingularToken(token));
    }
    if (!tagValue.includes(' ') && !tagValue.includes('-')) {
      seenWords.add(simpleSingularToken(tagValue));
    }

    for (const word of seenWords) {
      if (!word || word.length < 2) continue;
      if (!wordToTags.has(word)) wordToTags.set(word, new Map());
      const tagMap = wordToTags.get(word);
      if (!tagMap.has(tagKey)) {
        tagMap.set(tagKey, {
          key: tagKey,
          value: tagValue,
          product_count: v.product_count ?? 0
        });
      }
    }
  }

  const hints = [];
  for (const [word, tagMap] of wordToTags) {
    const tags = [...tagMap.values()];
    if (tags.length < minVariations) continue;
    hints.push({
      word,
      variation_count: tags.length,
      product_count: tags.reduce((sum, t) => sum + (t.product_count || 0), 0),
      tags
    });
  }

  hints.sort(
    (a, b) =>
      b.variation_count - a.variation_count ||
      b.product_count - a.product_count ||
      a.word.localeCompare(b.word)
  );
  return hints;
}

export function isCompoundTagValue(value) {
  const s = String(value || '').trim();
  if (!s) return false;
  if (s.includes(' ') || s.includes('-')) return true;
  if (s.length > 24) return true;
  if (CLAUSE_IN_TOKEN.test(s)) return true;
  return tokenizeTagEntry(s).length > 1;
}

export function buildAliasMapFromGroups(groups, field) {
  const map = {};
  for (const g of groups || []) {
    const groupField = g.field === 'mood' ? 'themes' : g.field;
    if (groupField !== field) continue;
    const canonical = String(g.canonical || '').trim();
    if (!canonical) continue;
    map[valueKeyForField(field, canonical)] = canonical;
    for (const a of g.aliases || []) {
      const k = String(a || '').trim();
      if (k) map[k] = canonical;
    }
  }
  return map;
}

export function buildWordMapRows(values, groups, field, localEdits = {}) {
  const aliasMap = buildAliasMapFromGroups(groups, field);
  return (values || []).map((v) => {
    const key = v.key;
    const resolved = aliasMap[key] ?? v.value;
    const canonical = localEdits[key] !== undefined ? localEdits[key] : resolved;
    return {
      key,
      value: v.value,
      product_count: v.product_count ?? 0,
      canonical
    };
  });
}
